import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { apiClient } from '~/lib/api-client'
import { CACHE_CONFIG } from '~/lib/cache-config'
import { localeToLanguageCode, type Locale } from '~/lib/i18n/shared'

export const RANKING_AGGREGATE_PAGE_SIZE = 24

export function parseCategoryAggregateId(snapshotId: string) {
  const match = snapshotId.match(/^(20\d{2})-CATEGORY-(.+)$/)
  if (!match || match[2].includes('-')) return null

  return {
    year: Number(match[1]),
    categorySourceId: match[2],
  }
}

export function buildCategoryAggregateId(year: number, categorySourceId: string) {
  return `${year}-CATEGORY-${categorySourceId}`
}

export function buildCategoryAggregateHref(year: number, categorySourceId: string) {
  return `/rankings/category/${year}/${encodeURIComponent(categorySourceId)}`
}

function createVirtualAwardSnapshot(snapshotId: string) {
  const match = snapshotId.match(/^(20\d{2})-AWARD-(GOLD|SILVER|BRONZE)-(\d+)$/)
  if (!match) return null

  const [, year, level, page] = match
  const titleLevel = level.charAt(0) + level.slice(1).toLowerCase()

  return {
    id: snapshotId,
    snapshotType: 'AWARD',
    year: Number(year),
    categoryId: null,
    regionId: null,
    awardLevel: level,
    page: Number(page),
    totalPages: null,
    url: `https://award.tabelog.com/${year}/restaurants/${level.toLowerCase()}`,
    totalShops: 0,
    snapshotData: { shops: [], metadata: {} },
    title: `The Tabelog Award ${year} ${titleLevel}`,
    description: null,
    createdAt: null,
    updatedAt: null,
    isVirtual: true,
  }
}

async function fetchAllSnapshotShops(snapshotId: string, locale: string) {
  const firstRes = await apiClient.shops['with-translation'][':locale'].$get({
    param: { locale },
    query: {
      snapshotId,
      page: '1',
      pageSize: '100',
      sort: 'id:asc',
    },
  })
  if (!firstRes.ok) throw new Error(`Failed to fetch shops for ranking ${snapshotId}`)

  const firstJson = await firstRes.json()
  const data = [...((firstJson as any).data || [])]
  const totalPages = (firstJson as any).pagination?.totalPages || 1

  for (let page = 2; page <= totalPages; page += 1) {
    const res = await apiClient.shops['with-translation'][':locale'].$get({
      param: { locale },
      query: {
        snapshotId,
        page: String(page),
        pageSize: '100',
        sort: 'id:asc',
      },
    })
    if (!res.ok) throw new Error(`Failed to fetch shops for ranking ${snapshotId}`)
    const json = await res.json()
    data.push(...((json as any).data || []))
  }

  return data
}

export function categoryAggregateQueryOptions(
  aggregate: { year: number, categorySourceId: string } | null,
  locale: string,
  selectedRegion?: string,
  page = 1,
) {
  const lang = localeToLanguageCode(locale as Locale)
  const aggregateKey = aggregate ? `${aggregate.year}-${aggregate.categorySourceId}` : 'invalid'

  return queryOptions({
    queryKey: ['ranking', 'category-aggregate', 'source-token-v3', aggregateKey, lang, selectedRegion || 'all', page],
    queryFn: async () => {
      if (!aggregate) return null

      const yearlyRes = await apiClient.rankings.yearly[':year'].$get({
        param: { year: String(aggregate.year) },
        query: { lang },
      })
      if (yearlyRes.status === 404) return null
      if (!yearlyRes.ok) throw new Error(`Failed to fetch rankings for ${aggregate.year}`)

      const yearly = await yearlyRes.json() as any
      const sourceCategory = (yearly.categories || []).find((category: any) => category.sourceId === aggregate.categorySourceId)
      if (!sourceCategory) return null

      const categories = (yearly.categories || []).filter((category: any) => category.name === sourceCategory.name)
      const allRegions = categories
        .flatMap((category: any) => category.regions || [])
        .filter((region: any) => region.totalShops > 0)

      const dedupedRegions = Array.from(
        new Map(allRegions.map((region: any) => [region.snapshotId, region])).values(),
      ) as any[]
      const selectedRegions = selectedRegion
        ? dedupedRegions.filter(region => region.sourceId === selectedRegion)
        : dedupedRegions

      const groups = []
      for (const region of selectedRegions) {
        groups.push({
          id: region.id,
          sourceId: region.sourceId,
          name: region.name,
          snapshotId: region.snapshotId,
          totalShops: region.totalShops,
          shops: await fetchAllSnapshotShops(region.snapshotId, locale),
        })
      }

      const entries = groups.flatMap(group =>
        group.shops.map((shop: any, index: number) => ({
          shop,
          region: {
            id: group.id,
            sourceId: group.sourceId,
            name: group.name,
            snapshotId: group.snapshotId,
          },
          rankingEntryNumber: index + 1,
        })),
      )
      const total = entries.length
      const totalPages = Math.max(1, Math.ceil(total / RANKING_AGGREGATE_PAGE_SIZE))
      const currentPage = Math.min(Math.max(1, page), totalPages)
      const start = (currentPage - 1) * RANKING_AGGREGATE_PAGE_SIZE
      const visibleEntries = entries.slice(start, start + RANKING_AGGREGATE_PAGE_SIZE)
      const visibleGroups = groups
        .map(group => ({
          ...group,
          entries: visibleEntries.filter(entry => entry.region.snapshotId === group.snapshotId),
          shops: undefined,
        }))
        .filter(group => group.entries.length > 0)

      return {
        id: aggregateKey,
        href: sourceCategory.href || buildCategoryAggregateHref(aggregate.year, sourceCategory.sourceId),
        year: aggregate.year,
        categorySourceId: aggregate.categorySourceId,
        categoryName: sourceCategory.name,
        selectedRegion,
        regions: dedupedRegions,
        groups: visibleGroups,
        totalEntries: total,
        totalRegions: dedupedRegions.length,
        pagination: {
          total,
          pageSize: RANKING_AGGREGATE_PAGE_SIZE,
          currentPage,
          totalPages,
        },
      }
    },
    staleTime: CACHE_CONFIG.reference.staleTime,
    gcTime: CACHE_CONFIG.reference.gcTime,
  })
}

export function rankingDetailQueryOptions(snapshotId: string, locale: string) {
  const lang = localeToLanguageCode(locale as Locale)

  return queryOptions({
    queryKey: ['ranking', 'detail', snapshotId, lang],
    queryFn: async () => {
      const virtualSnapshot = createVirtualAwardSnapshot(snapshotId)
      if (virtualSnapshot) return virtualSnapshot

      const res = await apiClient.rankings[':id'].$get({
        param: { id: snapshotId },
        query: { lang },
      })
      if (res.status === 404) return null
      if (!res.ok) throw new Error(`Failed to fetch ranking ${snapshotId}`)
      return res.json()
    },
    staleTime: CACHE_CONFIG.reference.staleTime,
    gcTime: CACHE_CONFIG.reference.gcTime,
  })
}

export function rankingShopsQueryOptions(snapshotId: string, locale: string, page = 1, hasSnapshot = true) {
  return queryOptions({
    queryKey: ['ranking', 'shops', snapshotId, locale, page, hasSnapshot],
    queryFn: async () => {
      if (!hasSnapshot) {
        return {
          data: [],
          pagination: {
            total: 0,
            pageSize: 0,
            currentPage: 1,
            totalPages: 0,
          },
        }
      }

      const res = await apiClient.shops['with-translation'][':locale'].$get({
        param: { locale },
        query: {
          snapshotId,
          page: String(page),
          pageSize: '100',
          sort: 'ratingScore:desc',
        },
      })
      if (!res.ok) throw new Error(`Failed to fetch shops for ranking ${snapshotId}`)
      return res.json()
    },
    staleTime: CACHE_CONFIG.reference.staleTime,
    gcTime: CACHE_CONFIG.reference.gcTime,
  })
}

export function useRankingDetail(snapshotId: string, locale: string) {
  const metadata = useSuspenseQuery(rankingDetailQueryOptions(snapshotId, locale))
  const shops = useSuspenseQuery(rankingShopsQueryOptions(snapshotId, locale, 1, Boolean(metadata.data)))
  return { metadata, shops }
}
